import React, { useRef, useState } from 'react';
import SectionLabel from '../ui/SectionLabel';
import testimonials from '../../constants/testimonials';
import './TestimonialsSection.css';

function StarRating({ rating }) {
  return (
    <div className="testimonial__stars" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={i < rating ? 'star star--filled' : 'star'}>
          ★
        </span>
      ))}
    </div>
  );
}

// Minimum horizontal drag (px) before a swipe counts as a slide change.
const SWIPE_THRESHOLD = 45;
// If the finger moves more vertically than this ratio relative to
// horizontal movement, treat it as a page-scroll, not a swipe.
const SWIPE_DIRECTION_RATIO = 1.2;

function TestimonialsSection() {
  const [active, setActive] = useState(0);

  const prev = () => setActive((p) => (p === 0 ? testimonials.length - 1 : p - 1));
  const next = () => setActive((p) => (p === testimonials.length - 1 ? 0 : p + 1));

  // ── Swipe state (mobile) ──
  const touchStart = useRef({ x: 0, y: 0 });
  const isSwiping = useRef(false);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const handleTouchStart = (e) => {
    const p = e.touches[0];
    touchStart.current = { x: p.clientX, y: p.clientY };
    isSwiping.current = true;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isSwiping.current) return;
    const p = e.touches[0];
    const dx = p.clientX - touchStart.current.x;
    const dy = p.clientY - touchStart.current.y;

    // If the gesture is clearly more vertical than horizontal, let the
    // page scroll normally instead of hijacking it as a swipe.
    if (Math.abs(dy) > Math.abs(dx) * SWIPE_DIRECTION_RATIO) {
      isSwiping.current = false;
      setIsDragging(false);
      setDragX(0);
      return;
    }

    if (Math.abs(dx) > 8) {
      e.preventDefault();
      setDragX(dx);
    }
  };

  const handleTouchEnd = () => {
    if (!isSwiping.current) {
      isSwiping.current = false;
      return;
    }
    isSwiping.current = false;
    setIsDragging(false);

    if (dragX <= -SWIPE_THRESHOLD) {
      next();
    } else if (dragX >= SWIPE_THRESHOLD) {
      prev();
    }
    setDragX(0);
  };

  const t = testimonials[active];

  return (
    <section className="testimonials" id="testimonials">
      <div className="testimonials__container">

        {/* ── Header ── */}
        <div className="testimonials__header">
          <SectionLabel text="💬 What Farmers Say" />
          <h2 className="testimonials__heading">
            Trusted by farmers{' '}
            <span className="testimonials__heading--accent">across India.</span>
          </h2>
        </div>

        {/* ── Card (swipeable on touch devices) ── */}
        <div
          className={`testimonials__card${isDragging ? ' testimonials__card--dragging' : ''}`}
          style={{
            transform: dragX ? `translateX(${dragX}px)` : undefined,
            transition: isDragging ? 'none' : undefined,
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
        >
          <div className="testimonials__quote-mark" aria-hidden="true">"</div>

          <StarRating rating={t.rating} />

          <p className="testimonials__text">{t.text}</p>

          <div className="testimonials__author">
            <div className="testimonials__avatar">{t.avatar}</div>
            <div className="testimonials__author-info">
              <span className="testimonials__name">{t.name}</span>
              <span className="testimonials__role">{t.role}</span>
            </div>
          </div>

          {/* ── Controls ── */}
          <div className="testimonials__controls">
            <button className="testimonials__arrow" onClick={prev} aria-label="Previous testimonial">
              ←
            </button>

            <div className="testimonials__dots">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  className={`testimonials__dot ${i === active ? 'testimonials__dot--active' : ''}`}
                  onClick={() => setActive(i)}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>

            <button className="testimonials__arrow" onClick={next} aria-label="Next testimonial">
              →
            </button>
          </div>
        </div>

        {/* ── All avatars row ── */}
        <div className="testimonials__faces">
          {testimonials.map((t, i) => (
            <button
              key={t.id}
              className={`testimonials__face ${i === active ? 'testimonials__face--active' : ''}`}
              onClick={() => setActive(i)}
              aria-label={`View ${t.name}'s testimonial`}
            >
              {t.avatar}
            </button>
          ))}
        </div>

      </div>
    </section>
  );
}

export default TestimonialsSection;